import React, {useEffect, useRef} from 'react';
import {NavigateFunction} from 'react-router-dom';
import {CardSpec, ListCards, ListRow} from '../../../shared/components/list-page/ListPageKit';
import {
  addressOneLine,
  displayPhone,
  formatApptTime,
  formatMoney,
  prettyStatusName,
} from '../../../shared/components/list-page/list-format';
import {estimateTotalCost} from '../../../shared/misc/estimate-helpers';
import {EstimateStatus, EstimateStatusType, EstimateWithClientAndCostDto} from '../../../shared/dtos/estimate.dto';
import {getAppointments} from '../../appointment/appointment.service';
import {AppointmentDto, AppointmentStatus, appointmentStatusLabel} from '../../appointment/dto/appointment.dto';
import {getExtendedEstimates} from '../../estimate/estimate.service';
import {getWorkOrders} from '../../work-order/work-order.service';
import {WorkOrderDto} from '../../work-order/dtos/work-order.dto';
import {getInvoices} from '../../invoice/invoice.service';
import {InvoiceDto} from '../../invoice/dtos/invoice.dto';

/*
 * "This client's full history" (Android's ClientHistoryController and
 * ClientHistorySection): everything ever filed under one client, at any
 * status, newest first, each deal under the one stage it is at now. It reads
 * the same company-wide lists the list pages read and filters by client;
 * there is no by-client endpoint for appointments, jobs or invoices.
 */

export type HistoryCategory = 'appointments' | 'estimates' | 'jobs' | 'invoices';

/** Pipeline order: the order the rows render in. */
const CATEGORIES: HistoryCategory[] = ['appointments', 'estimates', 'jobs', 'invoices'];

const CATEGORY_LABEL: Record<HistoryCategory, string> = {
  appointments: 'Estimate appointments',
  estimates: 'Estimates',
  jobs: 'Jobs',
  invoices: 'Invoices',
};

// The list pages' own card specs (their CARD_SPEC), so a history card reads like its list's card.
const SPECS: Record<HistoryCategory, CardSpec> = {
  appointments: {title: 211, status: 75, slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 267},
    {key: 'time', group: 'details', width: 259},
  ]},
  estimates: {title: 224, status: 117, slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 294},
    {key: 'total', group: 'money', width: 156},
  ]},
  jobs: {title: 221, status: 125, slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 314},
    {key: 'total', group: 'money', width: 124},
  ]},
  invoices: {title: 221, status: 66, slots: [
    {key: 'phone', group: 'who', width: 99},
    {key: 'address', group: 'details', width: 295},
    {key: 'total', group: 'money', width: 145},
  ]},
};

// The side menu's icons for the four lists.
const ICONS: Record<HistoryCategory, React.ReactNode> = {
  appointments: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5.4 4.5h13.2a2.4 2.4 0 0 1 2.4 2.4v11.7a2.4 2.4 0 0 1-2.4 2.4H5.4a2.4 2.4 0 0 1-2.4-2.4V6.9a2.4 2.4 0 0 1 2.4-2.4z"/>
    <path d="M3 9.6h18"/><path d="M7.6 2.4v3.2M16.4 2.4v3.2"/><path d="M6 12.4h4.6v4.2H6z" fill="currentColor" stroke="none"/>
  </svg>,
  estimates: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M13.6 2.6H6.6a2 2 0 0 0-2 2v14.8a2 2 0 0 0 2 2h10.8a2 2 0 0 0 2-2V8.6z"/><path d="M13.6 2.6v6h6"/>
    <path d="M12 10.6v7.6" strokeWidth={1.6}/>
    <path d="M13.9 12.6c-.35-.7-1.05-1.05-2-1.05-1.15 0-1.95.55-1.95 1.45s.75 1.2 1.95 1.45c1.3.27 2.15.7 2.15 1.65s-.85 1.55-2.15 1.55c-1.05 0-1.75-.4-2.1-1.05" strokeWidth={1.6}/>
  </svg>,
  jobs: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6.2 4.2h11.6a2.2 2.2 0 0 1 2.2 2.2v12.4a2.2 2.2 0 0 1-2.2 2.2H6.2a2.2 2.2 0 0 1-2.2-2.2V6.4a2.2 2.2 0 0 1 2.2-2.2z"/>
    <path d="M9 2.6h6v3.4H9z" fill="currentColor" strokeWidth={1.6}/><path d="M8.2 10.8h7.6M8.2 14.2h7.6M8.2 17.4h4.6" strokeWidth={1.8}/>
  </svg>,
  invoices: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 2.6l2.7 1.7 2.65-1.7L12 4.3l2.65-1.7 2.65 1.7L20 2.6v18.8l-2.7-1.7-2.65 1.7L12 19.7l-2.65 1.7-2.65-1.7L4 21.4z"/>
    <path d="M7.6 8.4h8.8M7.6 12h8.8M7.6 15.6h5.8" strokeWidth={1.8}/>
  </svg>,
};

/**
 * The stage an estimate's pipeline status puts its deal at (Android's
 * PipelineStage). A deal is one thing that moves appointment, estimate, job,
 * invoice, so it is listed once, under the stage it is at now.
 */
const stageOf = (status: EstimateStatusType): HistoryCategory => {
  switch (status) {
    // The estimate exists from the moment the appointment is booked; until someone starts it the deal is an appointment.
    case EstimateStatus.EstimateNotStarted: return 'appointments';
    case EstimateStatus.EstimateApproved:
    case EstimateStatus.WorkOrderScheduled:
    case EstimateStatus.WorkOrderInProgress:
    case EstimateStatus.WorkOrderCompleted:
    case EstimateStatus.WorkOrderCancelled: return 'jobs';
    case EstimateStatus.InvoiceCreated:
    case EstimateStatus.InvoiceSent:
    case EstimateStatus.InvoiceOverdue:
    case EstimateStatus.InvoicePaid:
    case EstimateStatus.ReceiptSent: return 'invoices';
    default: return 'estimates';
  }
};

const newestFirst = (a?: string | Date | null, b?: string | Date | null) =>
  (b ? new Date(b).getTime() : 0) - (a ? new Date(a).getTime() : 0);

export type ClientHistory = Partial<Record<HistoryCategory, ListRow[]>>;

/**
 * Loads the four lists in parallel and files each record under its stage. A
 * list the role can't open, or that fails, just isn't there: the history is
 * an extra way to reach records that all still have their own pages. The
 * cards are the list pages' own, and open what those pages' cards open.
 */
export async function loadClientHistory(
  navigate: NavigateFunction,
  clientId: number,
  canView: (category: HistoryCategory) => boolean,
): Promise<ClientHistory> {
  const quietly = <T, >(category: HistoryCategory, load: () => Promise<T[]>) =>
    canView(category) ? load().catch(() => [] as T[]) : Promise.resolve([] as T[]);
  const [appointments, estimates, jobs, invoices] = await Promise.all([
    quietly<AppointmentDto>('appointments', () => getAppointments(navigate)),
    quietly<EstimateWithClientAndCostDto>('estimates', () => getExtendedEstimates(navigate)),
    quietly<WorkOrderDto>('jobs', () => getWorkOrders(navigate)),
    quietly<InvoiceDto>('invoices', () => getInvoices(navigate)),
  ]);
  const live = estimates.filter(e => e.clientId === clientId && e.status !== EstimateStatus.EstimateDeleted);
  // Every stage of one deal shares the estimate id, and the estimate row carries the deal's status.
  const stages = new Map(live.map(e => [e.id, stageOf(e.status)]));

  const appointmentRows = appointments
    .filter(a => a.clientId === clientId)
    .filter(a => {
      const stage = stages.get(a.estimateId);
      // No estimate to read it from: its own status is all there is.
      return stage ? stage === 'appointments' : a.status !== AppointmentStatus.Completed;
    })
    // Newest first; unscheduled sink to the end.
    .sort((a, b) => (a.time ? 0 : 1) - (b.time ? 0 : 1) || newestFirst(a.time, b.time))
    .map((a): ListRow => {
      const time = formatApptTime(a.time);
      return {
        key: `appointment-${a.id}`,
        title: a.clientName?.trim() || `Appointment #${a.id}`,
        values: {
          phone: displayPhone(a.clientPhoneNumber),
          address: addressOneLine(a.estimateAddress, a.estimateCity, a.estimateState),
          time: time ? {label: 'Appt date/time', value: time} : {label: 'Appt date/time', value: 'Not scheduled', muted: true},
        },
        status: appointmentStatusLabel(a.status),
        onClick: () => navigate(`/appointments/${a.id}`),
      };
    });

  const estimateRows = live
    .filter(e => stageOf(e.status) === 'estimates')
    .sort((a, b) => newestFirst(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt))
    .map((e): ListRow => ({
      key: `estimate-${e.id}`,
      title: e.clientName?.trim() || e.estimateName?.trim() || `Estimate #${e.id}`,
      values: {
        phone: displayPhone(e.phoneNumber),
        address: addressOneLine(e.estimateAddress || e.clientAddress, e.estimateCity || e.clientCity, e.estimateState || e.clientState),
        total: {label: 'Estimate total', value: formatMoney(e.estimatedCost ?? 0)},
      },
      status: prettyStatusName(e.status),
      // Every status opens the record page, as the Estimates list does; /itemized is the old view (SQ-715).
      onClick: () => navigate(`/estimates/${e.id}`),
    }));

  const jobRows = jobs
    .filter(j => j.clientId === clientId)
    .filter(j => {
      // The work order, not the estimate, records the invoicing.
      const status = String(j.status);
      if (status === 'Deleted' || status === 'Invoiced' || status === 'PendingReInvoice') return false;
      return stages.get(j.estimateId) !== 'invoices';
    })
    .sort((a, b) => newestFirst(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt))
    .map((j): ListRow => {
      const estimate = j.estimateData;
      return {
        key: `job-${j.id}`,
        title: j.clientName?.trim() || `Job #${j.id}`,
        values: {
          phone: displayPhone(j.clientPhoneNumber),
          // The job site (the estimate's address) beats the client's home address.
          address: addressOneLine(estimate?.address || j.clientAddress, estimate?.city || j.clientCity, estimate?.state || j.clientState),
          total: estimate
            ? {label: 'Job total', value: formatMoney(estimateTotalCost(estimate))}
            : {label: 'Job total', value: 'Not set', muted: true},
        },
        status: j.status ? prettyStatusName(j.status) : undefined,
        onClick: () => navigate(`/jobs/${j.id}`),
      };
    });

  const invoiceRows = invoices
    .filter(i => i.clientId === clientId && String(i.status) !== 'Deleted')
    .sort((a, b) => newestFirst(a.updatedAt ?? a.createdAt, b.updatedAt ?? b.createdAt))
    .map((i): ListRow => ({
      key: `invoice-${i.id}`,
      title: i.clientName?.trim() || `Invoice #${i.companyInvoiceId}`,
      values: {
        phone: displayPhone(i.clientPhoneNumber),
        address: addressOneLine(i.clientAddress, i.clientCity, i.clientState),
        total: i.estimate
          ? {label: 'Invoice total', value: formatMoney(estimateTotalCost(i.estimate))}
          : {label: 'Invoice total', value: 'Not set', muted: true},
      },
      status: i.status ? prettyStatusName(i.status) : undefined,
      onClick: () => navigate(`/invoices/${i.id}`),
    }));

  return {appointments: appointmentRows, estimates: estimateRows, jobs: jobRows, invoices: invoiceRows};
}

/**
 * One row per kind that has records, with its count and View. A row opens
 * into that kind's cards; one kind is open at a time, and the open one's
 * header pins at the top of the record while its cards scroll under it.
 */
export const ClientHistorySection = ({history, open, onToggle}: {
  history: ClientHistory;
  open: HistoryCategory | null;
  onToggle: (category: HistoryCategory) => void;
}) => {
  const headers = useRef<Partial<Record<HistoryCategory, HTMLButtonElement | null>>>({});
  // Set by a press only, so coming back to the page leaves it where it was instead of jumping to the open row.
  const pressed = useRef<HistoryCategory | null>(null);
  const groups = CATEGORIES.filter(category => (history[category]?.length ?? 0) > 0);

  // Opening a kind starts at the top of its list, wherever the last one was left.
  useEffect(() => {
    if (open && pressed.current === open) headers.current[open]?.scrollIntoView({block: 'start', behavior: 'smooth'});
    pressed.current = null;
  }, [open]);

  // A kind with no records is not shown; with none at all, neither is the section.
  if (groups.length === 0) return null;
  return (
    <div className="cc-history">
      <div className="cc-history-rule"/>
      <div className="cc-history-title">This client's full history</div>
      {groups.map(category => {
        const rows = history[category] ?? [];
        const isOpen = open === category;
        return (
          <section key={category}>
            <button
              type="button"
              ref={el => { headers.current[category] = el; }}
              className={isOpen ? 'cc-category cc-category--open' : 'cc-category'}
              onClick={() => { pressed.current = category; onToggle(category); }}
              aria-expanded={isOpen}
            >
              <span className="cc-category-icon">{ICONS[category]}</span>
              <span className="cc-category-text">
                <div className="cc-category-label">{CATEGORY_LABEL[category]}</div>
                <div className="cc-category-count">{rows.length === 1 ? '1 record' : `${rows.length} records`}</div>
              </span>
              <span className="cc-view">{isOpen ? 'Hide' : 'View'}</span>
            </button>
            {isOpen && (
              <div className="cc-category-records lp">
                <ListCards rows={rows} spec={SPECS[category]}/>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
};
